import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { AnimationProps, PageAnimationTypes } from '../types';

import {
  getDefaultSpeedForPageAnimation,
  getPageAnimationComponent,
  isPartiallyInViewport,
} from './utils';

export type PageAnimationProps = {
  children: React.ReactNode;
  pageAnimation: PageAnimationTypes;
  showAnimations: boolean;
  animationProps?: AnimationProps;
  style?: React.CSSProperties;
  index?: number;
  /**
   * always trigger the animation on page load, regardless of the element's position on the page
   */
  showOnPageLoad?: boolean;
  /**
   * Value between 0 to 1
   * 0 means the element will count as intersecting with at least 1px on screen
   * 1 means the element will count as intersecting only when it is 100% on screen
   */
  intersectionObserverThreshold?: number;
  /** Amount of time to hide the element if the animation is being delayed
   * sometimes having hideDuringDelayValue === delay causes a flash before the animation starts
   * so this value can be slightly larger than whatever the actual delay value is
   */
  hideDuringDelayValue?: number;
  /**
   *  Subtracted from the browser height when determining if an element is in view
   *  Can be used when the browser is not the bounding box of the display (i.e. website preview)
   *  */
  inViewOffset?: number;
};

const hideDuringDelay = keyframes`
from {
  opacity: 0;
}

to {
  opacity: 0;
}`;

export const PageAnimationOuterWrapper = styled.div<{
  showAnimation: boolean;
  delay: number;
  hideDuringDelayValue?: number;
}>`
  ${(props) =>
    props.showAnimation === false &&
    css`
      opacity: 0;
    `};
  ${(props) =>
    props.delay > 0 &&
    css`
      animation: ${hideDuringDelay} ${props.hideDuringDelayValue || props.delay}s;
      -webkit-animation: ${hideDuringDelay} ${props.hideDuringDelayValue || props.delay}s;
    `};
`;

export const PageAnimation = (props: PageAnimationProps) => {
  const {
    animationProps,
    children,
    showAnimations,
    pageAnimation,
    style,
    index,
    showOnPageLoad = false,
    intersectionObserverThreshold = 0.2,
    hideDuringDelayValue,
    inViewOffset,
  } = props;
  const { isAbsoluteImage, speed } = animationProps || {};

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [shouldTriggerAnimation, setShouldTriggerAnimation] = useState(showOnPageLoad);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    setIsInView(isPartiallyInViewport(wrapperRef, inViewOffset));
  }, [wrapperRef, inViewOffset]);

  // fade in absolute images for all animations types except wipe
  const animationToUse =
    isAbsoluteImage && pageAnimation !== 'page-animation-wipe'
      ? 'page-animation-fade'
      : pageAnimation;
  const PageAnimationComponent = getPageAnimationComponent(animationToUse);

  const defaultSpeed = getDefaultSpeedForPageAnimation(animationToUse);
  const speedToUse = speed || defaultSpeed;

  // we want to delay the animation if it is part of a list (has an index) and is currently partially visible
  // if an element has an index but is not in view, we do not want to delay, as we want it to immediately trigger on scroll
  const delay =
    index && index > 0 && shouldTriggerAnimation && isInView
      ? index * speedToUse
      : animationProps?.delay;

  useEffect(() => {
    // trigger animation when elements come into view
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldTriggerAnimation(true);
          }
        });
      },
      {
        threshold: intersectionObserverThreshold,
      }
    );

    if (wrapperRef?.current) observer.observe(wrapperRef.current);

    return () => {
      observer.disconnect();
    };
  }, [wrapperRef, intersectionObserverThreshold]);

  if (!showAnimations || !PageAnimationComponent) {
    return <Fragment> {children} </Fragment>;
  }

  return (
    <PageAnimationOuterWrapper
      ref={wrapperRef}
      showAnimation={shouldTriggerAnimation}
      style={style}
      delay={delay || 0}
      hideDuringDelayValue={hideDuringDelayValue}
    >
      {shouldTriggerAnimation ? (
        <PageAnimationComponent {...animationProps} delay={delay || 0}>
          {children}
        </PageAnimationComponent>
      ) : (
        children
      )}
    </PageAnimationOuterWrapper>
  );
};
